import React, { useState, useEffect, useCallback, useRef } from 'react'
import { makeStyles } from 'tss-react/mui'
import { ApiTableSkeleton, ColumnAmount } from '../Skeletons/ApiTableSkeleton'
import { Table } from '../Table/Table'
import { ErrorSkeleton } from '../Error/ErrorMessage'
import { useCustomTranslation } from '../../Hooks/useCustomTranslation'
import { useTypedSelector } from '../../State/RootReducer'
import { Payload } from '../../Hooks/AuthFetch/AuthFetch'
import { useFetch, useFetchApiObject } from '../../Hooks/useFetch'
import { Paper } from '../Paper'
import { Pagination } from '../Table'
import { ApiTableColumn, FetchProps } from './ApiView'
import { InternalFilter } from './ApiViewModels'
import { CurrentSort, SortingConfirguration, TableCellPadding } from '../Table/types'
import { deepEqual } from 'fast-equals'

export interface ApiTableProps<U> {
  fetchProps: FetchProps
  columns: ApiTableColumn<U>[]
  customColumns?: ApiTableColumn<U>[]
  hasColoredTableColumns?: boolean
  customFunctionalities?: ((item: U) => React.ReactNode)[]
  filterString?: string
  oneFilterString?: string
  onSelect?: (item: U) => void
  noData?: string
  internalFilters: InternalFilter[]
  withLoading?: boolean
  bodyElevation?: number
  headerElevation?: number
  footerElevation?: number
  collapsibleContent?: (row: U) => React.ReactNode
  collapsibleContentPadding?: TableCellPadding
  onAfterFetch?: (data?: U[], count?: number) => void
  containerClassName?: string
  headerClassName?: string
  muiTableClassName?: string
  bodyClassName?: string
  setCurrPage?: (value: number) => void
  currPage?: number
  isMobilePaginator?: boolean
  customFunctionalityClass?: string
  blankView?: JSX.Element
  initialSort?: SortingConfirguration<U>
  onSortChange?: (newSort: SortingConfirguration<U>) => void
  initialPageLimit?: number
  onPageLimitChange?: (newPageLimit: number) => void
  /** default: true */
  resetPageAfterDataChange?: boolean
  customPayload?: Record<string, unknown>
  rowsPerPageOptions?: number[]
}

const useStyles = makeStyles()(theme => ({
  margin: {
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
}))

/**
 * @template U type of the converted fetched data item (@see objectApiResult)
 *
 */
const ApiTable = <U extends { [key: string]: unknown }>({
  fetchProps,
  columns,
  customColumns = [],
  hasColoredTableColumns,
  customFunctionalities,
  filterString,
  oneFilterString,
  onSelect,
  internalFilters,
  bodyElevation,
  headerElevation,
  footerElevation,
  collapsibleContent,
  collapsibleContentPadding,
  onAfterFetch,
  headerClassName,
  muiTableClassName,
  bodyClassName,
  setCurrPage,
  currPage,
  isMobilePaginator,
  containerClassName,
  customFunctionalityClass,
  blankView,
  initialSort,
  onSortChange,
  initialPageLimit = 10,
  onPageLimitChange,
  resetPageAfterDataChange = true,
  customPayload,
  noData,
  rowsPerPageOptions,
}: ApiTableProps<U>): React.ReactElement => {
  const { t } = useCustomTranslation()
  const { classes } = useStyles()
  const firstPayload: Payload = { ...customPayload }
  const { url, endpoint, token, filter, connect, orderBy, offset, searchPhrase } = fetchProps
  const order = orderBy ? orderBy.split(',') : undefined

  const getInitialSortFromOrderBy = (): SortingConfirguration<U> => {
    return (
      order?.map(entry => ({
        name: columns.find(column => column.name === entry.substring(1))?.name ?? columns[0]?.name,
        direction: entry?.substring(0, 1) === '-' ? 'desc' : 'asc',
      })) ?? {
        name: columns[0]?.name,
        direction: 'asc',
      }
    )
  }

  const [currentSort, setCurrentSort] = useState<SortingConfirguration<U>>(initialSort ?? getInitialSortFromOrderBy())

  const lastSort = useRef<SortingConfirguration<U>>()

  if (!deepEqual(lastSort.current, currentSort)) {
    lastSort.current = currentSort
    onSortChange?.(currentSort)
  }

  const getSortValue = useCallback(() => {
    if (columns.every(c => c.sortable === false)) {
      return
    }

    let currentSortingConfig: CurrentSort<U>[]

    if (Array.isArray(currentSort)) {
      currentSortingConfig = currentSort
    } else {
      currentSortingConfig = [currentSort]
    }

    const columnNames = currentSortingConfig.map(sort => {
      return columns?.find(column => column.name === sort.name)?.name
    })

    if (columnNames) {
      const sortStringArray: string[] = []
      columnNames.forEach(name => {
        const currentDirection = currentSortingConfig.find(sort => sort.name === name)?.direction
        const sortName = name?.toString()
        if (sortName) sortStringArray.push((currentDirection === 'desc' ? '-' : '+') + sortName)
      })

      return sortStringArray.join(',')
    }
  }, [columns, currentSort])

  if (filter) firstPayload.filter = filter
  if (filterString) firstPayload.filter = firstPayload.filter ? firstPayload.filter + ',' + filterString : filterString
  if (oneFilterString) firstPayload.filterOr = firstPayload.filterOr ? firstPayload.filterOr + ',' + oneFilterString : oneFilterString
  if (connect) firstPayload.connect = connect
  if (searchPhrase) firstPayload.searchPhrase = searchPhrase
  const firstCountPayload = { ...firstPayload }
  /** count does not need these payloads */
  firstPayload.limit = fetchProps.limit ?? initialPageLimit
  firstPayload.offset = offset ?? 0
  firstPayload.sort = getSortValue()

  const refresh = useTypedSelector(state => state.apiView?.refresh)
  const [page, setPage] = useState(currPage ?? 0)
  const [internalFiltersActive, setInternalFiltersActive] = useState(false)
  const [payload, setPayload] = useState<Payload>(firstPayload)
  const [countPayload, setCountPayload] = useState<Payload>(firstCountPayload)

  const [limit, setLimit] = useState(fetchProps.limit ?? initialPageLimit)

  const updatePageLimit = (newPageLimit: number): void => {
    setLimit(newPageLimit)
    onPageLimitChange?.(newPageLimit)
  }

  useEffect(() => {
    updatePageLimit(initialPageLimit)
  }, [initialPageLimit]) //eslint-disable-line

  const nonSortableColumnLabels = columns.filter(column => column.sortable === false).map(column => column.label)

  const [fetchCount, fetchCountLoading] = useFetch<{ count: number }>({
    url: `${url}/stats`,
    payload: countPayload,
    token,
    endpoint,
    dependencies: [url, connect, refresh, searchPhrase],
  })

  const [fetchedData, fetchedDataLoading, fetchedDataError] = useFetchApiObject<U[]>({
    url,
    payload,
    token,
    endpoint,
    dependencies: [offset, refresh, searchPhrase, connect, currentSort],
  })

  useEffect(() => {
    if (page !== 0 && fetchedData?.length === 0 && resetPageAfterDataChange) {
      setPage(0)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchedData, page, resetPageAfterDataChange])

  useEffect(() => {
    onAfterFetch?.(fetchedData, fetchCount?.count)
  }, [fetchCount, fetchedData]) //eslint-disable-line

  useEffect(() => {
    setInternalFiltersActive(internalFilters?.find(filter => filter.value) ? true : false)
    setPage(0)
  }, [internalFilters, filterString])

  // go to page 0 if internal filters are active
  useEffect(() => {
    let isMounted = true
    if (isMounted) {
      setPage(0)
    }
    return () => {
      isMounted = false
    }
  }, [filter])

  useEffect(() => {
    let isMounted = true

    /** Create and set the payload if the count > 0 to avoid redundant requests. */
    if (fetchCount && fetchCount.count > 0) {
      const payload: Payload = {
        ...customPayload,
        limit: internalFiltersActive ? undefined : limit,
        offset: internalFiltersActive ? 0 : page * limit,
      }

      if (filter) payload.filter = filter
      if (searchPhrase) payload.searchPhrase = searchPhrase
      if (filterString) payload.filter = payload.filter ? payload.filter + ',' + filterString : filterString
      if (oneFilterString) payload.filterOr = payload.filterOr ? payload.filterOr + ',' + oneFilterString : oneFilterString
      if (connect) payload.connect = connect
      if (offset) payload.offset = offset
      if (currentSort) {
        payload.sort = getSortValue()
      }

      if (isMounted) setPayload(payload)

      return () => {
        isMounted = false
      }
    }
  }, [
    limit,
    url,
    filter,
    connect,
    currentSort,
    refresh,
    page,
    filterString,
    oneFilterString,
    internalFiltersActive,
    columns,
    offset,
    searchPhrase,
    getSortValue,
    fetchCount,
    customPayload,
  ])

  useEffect((): (() => void) => {
    let isMounted = true

    const payload: Payload = { ...customPayload }

    if (filter) payload.filter = filter
    if (filterString) payload.filter = payload.filter ? payload.filter + ',' + filterString : filterString
    if (oneFilterString) payload.filterOr = payload.filterOr ? payload.filterOr + ',' + oneFilterString : oneFilterString
    if (connect) payload.connect = connect
    if (offset) payload.offset = offset
    if (searchPhrase) payload.searchPhrase = searchPhrase
    if (isMounted) setCountPayload(payload)

    return () => (isMounted = false)
  }, [url, connect, filter, refresh, filterString, internalFiltersActive, offset, searchPhrase, oneFilterString, customPayload])

  useEffect(() => {
    setPage(currPage ?? 0)
  }, [filter, currPage, searchPhrase])

  if (fetchedDataError) {
    return (
      <Paper>
        <ErrorSkeleton title={t('errors.dataError')} />
      </Paper>
    )
  }

  if (fetchCountLoading || fetchedDataLoading || fetchedData === undefined) {
    return (
      <Paper elevation={bodyElevation} data-qa={'table-data-loader'}>
        <ApiTableSkeleton
          columnAmount={(columns.length + (customFunctionalities?.length ?? 0)) as ColumnAmount}
          withFilter={false}
        ></ApiTableSkeleton>
      </Paper>
    )
  }

  const filteredData: U[] = internalFiltersActive
    ? fetchedData.filter(item =>
        internalFilters.reduce<boolean>(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (prev, currFilter) => currFilter.filterFunction((item as any)[currFilter.name], currFilter.value) && prev,
          true
        )
      )
    : fetchedData

  if (blankView && filteredData.length === 0) {
    return blankView
  }

  return (
    <>
      <Table
        header={[...columns, ...customColumns].map(elem => ({
          key: String(elem.name),
          label: elem.label,
        }))}
        data={
          filteredData
            ?.map(item => columns.map(column => column.value(item)))
            .slice(internalFiltersActive ? page * limit : 0, internalFiltersActive ? (page + 1) * limit : undefined) ?? []
        }
        customFunctionalities={customFunctionalities?.map(functionality => {
          return filteredData.map(item => functionality(item))
        })}
        hasColoredTableColumns={hasColoredTableColumns}
        // fire the callback with the selected data row
        onSelect={onSelect ? index => onSelect(filteredData[index]) : undefined}
        currentSort={currentSort}
        setCurrentSort={setCurrentSort}
        bodyElevation={bodyElevation}
        headerElevation={headerElevation}
        collapsibleContent={collapsibleContent ? index => collapsibleContent(filteredData[index]) : undefined}
        collapsibleContentPadding={collapsibleContentPadding}
        bodyClassName={bodyClassName}
        customFunctionalityClass={customFunctionalityClass}
        headerClassName={headerClassName}
        containerClassName={containerClassName}
        muiTableClassName={muiTableClassName}
        nonSortableColumns={nonSortableColumnLabels}
        noData={noData}
      />
      <Pagination
        count={internalFiltersActive ? filteredData.length : fetchCount?.count ?? 0}
        limit={limit}
        setPage={setPage}
        setCurrPage={setCurrPage}
        page={page}
        setLimit={updatePageLimit}
        className={classes.margin}
        elevation={footerElevation}
        isMobilePaginator={isMobilePaginator}
        rowsPerPageOptions={rowsPerPageOptions}
      />
    </>
  )
}

export default ApiTable
